import ButtonBase from '@material-ui/core/ButtonBase';
import MuiMenu, { MenuProps as MuiMenuProps } from '@material-ui/core/Menu';
import React, { Children, cloneElement } from 'react';

import StyledIconButton from '../Buttons/IconButton';
import useAnchorState from '../hooks/useAnchorState';
import ArrowDownIcon from '../images/arrow-down.svg';
import useStyles from './styles';

const TRIGGER_TYPE = {
  icon: 'icon',
};

export interface MenuBaseProps extends MuiMenuProps {
  ariaLabel: string;
  autoFocus: boolean;
  closeOnClick: boolean;
  dataTestIds?: {
    popover?: string;
    trigger?: string;
  };
  defaultIconColor: boolean;
  disableAutoFocusItem: boolean;
  id: string;
  isDisabled: boolean;
  menuWidth: number;
  showDropdownArrow: boolean;
  triggerEl: React.ReactNode;
  triggerType: 'icon';
}

export type MenuProps = Partial<MenuBaseProps>;

/**
 * Prefer using MUI's `Menu` directly instead
 * @deprecated
 */
export const Menu: React.FC<MenuProps> = ({
  anchorOrigin = { horizontal: 'left', vertical: 'bottom' },
  ariaLabel,
  children,
  className,
  dataTestIds = {},
  id,
  isDisabled = false,
  menuWidth,
  showDropdownArrow = false,
  transformOrigin = { horizontal: 'left', vertical: 'top' },
  triggerEl,
  triggerType,
  autoFocus = false,
  disableAutoFocusItem = false,
  closeOnClick = false,
  defaultIconColor = false,
  MenuListProps,
  ...otherProps
}: MenuProps) => {
  const { anchorEl, toggle, handleClose } = useAnchorState();
  const classes = useStyles({ menuIsOpen: !!anchorEl, menuWidth });

  const renderTriggerEl = () => {
    switch (triggerType) {
      case TRIGGER_TYPE.icon: {
        return (
          <StyledIconButton
            aria-controls={id}
            aria-haspopup="true"
            aria-label={ariaLabel}
            className={className}
            data-testid={dataTestIds.trigger}
            defaultIconColor={defaultIconColor}
            href={undefined}
            isDisabled={isDisabled}
            isExternalLink={undefined}
            isSelected={undefined}
            onClick={toggle}
            primary={undefined}
            size={undefined}
          >
            {triggerEl}
          </StyledIconButton>
        );
      }
      default: {
        return (
          <ButtonBase
            aria-controls={id}
            aria-haspopup="true"
            aria-label={ariaLabel}
            className={className}
            data-testid={dataTestIds.trigger}
            disabled={isDisabled}
            id={id}
            onClick={toggle}
          >
            {triggerEl}
            {!isDisabled && showDropdownArrow && (
              <img alt="arrow" className={classes.dropdownArrow} src={ArrowDownIcon} />
            )}
          </ButtonBase>
        );
      }
    }
  };

  const handleCloseMenu = (onClick?: () => void) => {
    if (onClick) onClick();
    handleClose();
  };

  return (
    <>
      {renderTriggerEl()}
      <MuiMenu
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        autoFocus={autoFocus}
        classes={{
          list: classes.menuContent,
          paper: classes.menuRoot,
        }}
        data-testid={dataTestIds.popover}
        disableAutoFocusItem={disableAutoFocusItem}
        elevation={0}
        getContentAnchorEl={null}
        MenuListProps={MenuListProps}
        onClose={handleClose}
        open={!!anchorEl}
        transformOrigin={transformOrigin}
      >
        {closeOnClick
          ? Children.map(
              children,
              /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
              (child: any) =>
                child &&
                cloneElement(child, {
                  ...child.props,
                  onClick: () => handleCloseMenu(child.props.onClick),
                }),
            )
          : children}
      </MuiMenu>
    </>
  );
};

Menu.defaultProps = {
  anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
  autoFocus: false,
  closeOnClick: false,
  defaultIconColor: false,
  disableAutoFocusItem: false,
  isDisabled: false,
  showDropdownArrow: true,
  transformOrigin: { horizontal: 'left', vertical: 'top' },
};

export default Menu;
